import { useQuery } from '@tanstack/react-query';
import { getCurrentAppVersion } from 'utils/appVersion';

export const VERSION_MANIFEST_URL = '/portal-assets/version.json';
export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch(`${VERSION_MANIFEST_URL}?t=${Date.now()}`, { cache: 'no-store' });

    if (!response.ok) {
      return null;
    }

    const data: unknown = await response.json();

    if (typeof data !== 'object' || data === null) {
      return null;
    }

    const { version } = data as { version?: unknown };

    return typeof version === 'string' && version ? version : null;
  } catch {
    // Intentionally swallow errors. Background version check should not cause any user facing error messages.
    return null;
  }
}

interface UseVersionCheckOptions {
  enabled?: boolean;
}

export function useVersionCheck({ enabled = true }: UseVersionCheckOptions = {}) {
  const currentVersion = getCurrentAppVersion();

  const { data: deployedVersion } = useQuery({
    queryKey: ['deployed-app-version'],
    queryFn: fetchDeployedVersion,
    enabled: enabled && Boolean(currentVersion),
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: query =>
      query.state.data && query.state.data !== currentVersion ? false : VERSION_POLL_INTERVAL_MS,
  });

  return { isUpdateAvailable: Boolean(deployedVersion && deployedVersion !== currentVersion) };
}
